/**
 * DEPLETIONS-INVENTORY MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// ImportModal.jsx — spreadsheet upload + preview + commit for depletions and
// inventory. This is the "Import" button the module's ARCHITECTURE.md described
// for months while no such UI existed (backlog item 27): the templates in
// utils/importTemplates.js and the write functions in utils/depletionService.js
// were both written and then left with zero callers, which made Google Sheets
// the only way data could get in. A customer without a Google account had no
// ingest path at all.
//
// THE RULE THIS FILE EXISTS TO NOT BREAK (backlog items 26 / 52):
// NEVER clear before validating. The Sheets inventory sync deleted the account's
// whole table and only then discovered it had parsed nothing — a sheet whose
// states no longer matched reference data wiped everything and reported "ok".
// Here nothing is written until the user has seen the matched/skipped counts,
// and a zero-match parse cannot be committed at all.
//
// AND NEVER CLEAR BEFORE WRITING EITHER (backlog APP-334). Validating first
// still left the commit as clear-then-insert, so a dropped connection between
// the two deleted the period and wrote nothing — reported as "Import failed.".
// The commit now goes through importDepletionsSafely / importInventorySafely,
// which insert first and only retire the superseded rows once every batch has
// landed; this file's job is to turn the outcome they return into a sentence
// that says whether the user's data is intact, doubled, or replaced.
//
// Header names are the same aliases the sheets-sync edge function accepts, so a
// file that works in one path works in the other.

import React, { useState, useRef, useCallback } from "react";
import {
  importDepletionsSafely, importInventorySafely, IMPORT_OUTCOME,
} from "../utils/depletionService.js";
import { downloadDepletionsTemplate, downloadInventoryTemplate } from "../utils/importTemplates.js";
import "./ImportModal.css";

const MONTH_NUM = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };
const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Same two forms the edge function accepts: "Jan-25" and an Excel date serial.
function parseMonthCol(header) {
  const str = String(header ?? "").trim();
  const m = str.match(/^([A-Za-z]{3})-(\d{2})$/);
  if (m) {
    const month = MONTH_NUM[m[1].toLowerCase()];
    return month ? { month, year: 2000 + parseInt(m[2], 10) } : null;
  }
  const serial = parseInt(str, 10);
  if (!isNaN(serial) && serial > 40000 && serial < 60000) {
    const d = new Date((serial - 25569) * 86400 * 1000);
    return { month: d.getUTCMonth() + 1, year: d.getUTCFullYear() };
  }
  return null;
}

function findCol(headersLower, names) {
  for (const n of names) {
    const i = headersLower.indexOf(n);
    if (i >= 0) return i;
  }
  return -1;
}

const COLS = {
  distributor: ["distributor"],
  state: ["state", "state name", "state_name"],
  flavor: ["flavor"],
  size: ["bottle size", "size", "bottle_size"],
  pkg: ["package", "pack", "package size", "pack size"],
  inventory: ["current inventory", "inventory", "current_inventory", "cases", "on hand", "on_hand", "qty", "quantity"],
  chain: ["chain", "chain name", "chain_name", "banner"],
  account: ["account", "account name", "account_name", "store", "store name", "outlet", "retailer"],
  address: ["address", "account address", "account_address", "street", "address 1", "address line 1"],
  city: ["city", "account city", "account_city"],
};

// SheetJS on demand — it is ~938 kB, and this module already lazy-loads it for
// export (backlog item 18). Do not turn this into a static import.
async function loadXLSX() {
  const m = await import("xlsx");
  return m.read ? m : (m.default || m);
}

function buildLookups({ states, flavors, sizes, packSizes }) {
  // `stateByName` is the no-distributor-column fallback and is deliberately
  // built LAST-WRITE-NEVER: a state sold through two distributors is AMBIGUOUS,
  // so it holds null and the row is skipped rather than filed under whichever
  // sales_states row happened to come back last (backlog 331).
  const stateByKey = {}, stateByName = {};
  (states || []).forEach((s) => {
    const name = String(s.state_name).toLowerCase();
    stateByKey[`${name}|${String(s.distributor || "").toLowerCase()}`] = s;
    stateByName[name] = name in stateByName ? null : s;
  });
  const byName = (rows) => {
    const m = {};
    (rows || []).forEach((r) => { m[String(r.name).toLowerCase()] = r; });
    return m;
  };
  return {
    stateByKey, stateByName,
    flavorByName: byName(flavors), sizeByName: byName(sizes), packByName: byName(packSizes),
  };
}

// Backlog 331: a NAMED distributor that doesn't match is a mismatch, not a
// near-miss. Falling through to the state-only lookup filed the row under some
// other distributor's business — and stamped that distributor's
// last_depletions_update — while the import reported a clean parse. The
// state-only lookup now applies ONLY when the sheet named no distributor at
// all, and only when that state is sold through exactly one of them.
const matchState = (ref, stateRaw, distRaw) => {
  const name = stateRaw.toLowerCase();
  const exact = ref.stateByKey[`${name}|${String(distRaw || "").toLowerCase()}`];
  if (exact) return exact;
  if (String(distRaw || "").trim()) return null;
  return ref.stateByName[name] || null;
};

const cellStr = (r, i, cap = 200) =>
  i >= 0 ? (String(r[i] ?? "").trim().slice(0, cap) || null) : null;

// ── Product-dimension matching (backlog item 104) ────────────────────────────
// An unmatched Flavor / Size / Package used to resolve to null and the row was
// written anyway, so a misspelled flavor — or a SKU renamed in System Settings
// and not in the sheet — imported every row attached to NO SKU while this modal
// reported a clean parse. Those rows then collect under the grid's "—" bucket
// (reads as a data-entry oddity, not a failed import) and contribute nothing to
// the Retail Product Finder, whose product list is derived from what was
// actually depleted at each door.
//
// Deliberately NOT the unmatched-STATE behaviour: skipping the row would throw
// away a real case number the distributor reported. The row is kept, and every
// miss is counted and NAMED — the user has to go fix one specific spreadsheet
// cell, and a bare count doesn't tell them which.
//
// A miss only counts when the sheet actually supplied a value. An absent column
// or a blank cell means the file simply doesn't carry that dimension, which is a
// legitimate shape (state-level sheets) rather than a typo.
const DIM_LABELS = { flavor: "Flavor", size: "Bottle Size", pack: "Package" };
const DIM_MAX_VALUES = 200;   // a garbage column must not build a 50k-entry Map
const DIM_TOP_N = 6;

function makeDimTracker() {
  const misses = { flavor: new Map(), size: new Map(), pack: new Map() };
  const rowCounts = { flavor: 0, size: 0, pack: 0 };
  let pending = [];
  return {
    begin() { pending = []; },
    /** Look a dimension up, remembering a miss until the row is committed. */
    resolve(dim, lookup, idx, r) {
      if (idx < 0) return null;                       // column not in the file
      const raw = String(r[idx] ?? "").trim();
      if (!raw) return null;                          // blank cell, nothing claimed
      const hit = lookup[raw.toLowerCase()];
      if (!hit) pending.push([dim, raw.slice(0, 80)]);
      return hit || null;
    },
    /** Fold the row's misses in. Deferred so a row that contributes no values
     *  (every month blank) is never reported as a broken import. */
    commit() {
      for (const [dim, raw] of pending) {
        const m = misses[dim];
        if (m.has(raw) || m.size < DIM_MAX_VALUES) m.set(raw, (m.get(raw) || 0) + 1);
        rowCounts[dim]++;
      }
      const had = pending.length > 0;
      pending = [];
      return had;
    },
    discard() { pending = []; },
    summary() {
      return Object.keys(DIM_LABELS)
        .filter((k) => rowCounts[k] > 0)
        .map((k) => ({
          key: k,
          label: DIM_LABELS[k],
          rows: rowCounts[k],
          distinct: misses[k].size,
          values: [...misses[k].entries()].sort((a, b) => b[1] - a[1]).slice(0, DIM_TOP_N),
        }));
    },
  };
}

/** Parse a depletions workbook into insertable rows + the periods they touch. */
function parseDepletions(values, ref) {
  if (!values || values.length < 2) throw new Error("That file has no data rows.");
  const headers = (values[0] || []).map((h) => String(h));
  const hLower = headers.map((h) => h.toLowerCase().trim());

  const distIdx = findCol(hLower, COLS.distributor);
  const stateIdx = findCol(hLower, COLS.state);
  if (stateIdx < 0) throw new Error("No 'State' column found.");
  const flavorIdx = findCol(hLower, COLS.flavor);
  const sizeIdx = findCol(hLower, COLS.size);
  const pkgIdx = findCol(hLower, COLS.pkg);
  const chainIdx = findCol(hLower, COLS.chain);
  const acctIdx = findCol(hLower, COLS.account);
  const addrIdx = findCol(hLower, COLS.address);
  const cityIdx = findCol(hLower, COLS.city);

  const monthCols = headers
    .map((h, i) => { const p = parseMonthCol(h); return p ? { idx: i, ...p } : null; })
    .filter(Boolean);
  if (!monthCols.length) throw new Error("No month columns found (expected headers like Jan-25).");

  const out = [];
  const periods = {};
  const distributors = new Set();
  const unmatched = new Map();
  const dims = makeDimTracker();
  let matchedRows = 0, skippedRows = 0, dimMissRows = 0;

  for (const r of values.slice(1)) {
    const stateRaw = String(r[stateIdx] ?? "").trim();
    if (!stateRaw) continue;                       // blank line, not a data row
    const distRaw = distIdx >= 0 ? String(r[distIdx] ?? "").trim() : "";
    const st = matchState(ref, stateRaw, distRaw);
    if (!st) {
      skippedRows++;
      const k = distRaw ? `${stateRaw} / ${distRaw}` : stateRaw;
      unmatched.set(k, (unmatched.get(k) || 0) + 1);
      continue;
    }
    dims.begin();
    const flavor = dims.resolve("flavor", ref.flavorByName, flavorIdx, r);
    const size = dims.resolve("size", ref.sizeByName, sizeIdx, r);
    const pack = dims.resolve("pack", ref.packByName, pkgIdx, r);
    if (st.distributor) distributors.add(st.distributor);

    const chainName = cellStr(r, chainIdx, 160);
    const accountName = cellStr(r, acctIdx, 200);
    const accountAddress = cellStr(r, addrIdx, 300);
    const accountCity = cellStr(r, cityIdx, 120);

    let hadValue = false;
    for (const mc of monthCols) {
      const val = parseFloat(String(r[mc.idx] ?? "").replace(/,/g, "")) || 0;
      if (!val) continue;                          // blank cell → not a zero, just absent
      hadValue = true;
      periods[`${mc.year}-${mc.month}`] = { year: mc.year, month: mc.month };
      out.push({
        year: mc.year, month: mc.month,
        stateId: st.id, flavorId: flavor?.id ?? null, sizeId: size?.id ?? null,
        packSizeId: pack?.id ?? null, caseSales: val,
        chainName, accountName, accountAddress, accountCity,
      });
    }
    // Only a row that actually contributed a number can be a broken import.
    if (hadValue) { matchedRows++; if (dims.commit()) dimMissRows++; }
    else dims.discard();
  }

  return {
    kind: "depletions",
    rows: out,
    periods: Object.values(periods),
    distributors: [...distributors],
    matchedRows, skippedRows, dimMissRows,
    unmatched: [...unmatched.entries()].sort((a, b) => b[1] - a[1]).slice(0, DIM_TOP_N),
    dimUnmatched: dims.summary(),
  };
}

/** Parse an inventory workbook. Inventory is a full replace, so the zero-match
 *  guard matters even more here — that is precisely backlog item 26. */
function parseInventory(values, ref) {
  if (!values || values.length < 2) throw new Error("That file has no data rows.");
  const headers = (values[0] || []).map((h) => String(h));
  const hLower = headers.map((h) => h.toLowerCase().trim());

  const distIdx = findCol(hLower, COLS.distributor);
  const stateIdx = findCol(hLower, COLS.state);
  if (stateIdx < 0) throw new Error("No 'State' column found.");
  const invIdx = findCol(hLower, COLS.inventory);
  if (invIdx < 0) throw new Error("No 'Current Inventory' column found.");
  const flavorIdx = findCol(hLower, COLS.flavor);
  const sizeIdx = findCol(hLower, COLS.size);
  const pkgIdx = findCol(hLower, COLS.pkg);

  const out = [];
  const distributors = new Set();
  const unmatched = new Map();
  const dims = makeDimTracker();
  let skippedRows = 0, dimMissRows = 0;

  for (const r of values.slice(1)) {
    const stateRaw = String(r[stateIdx] ?? "").trim();
    if (!stateRaw) continue;
    const invCell = String(r[invIdx] ?? "").trim();
    if (invCell === "") continue;                  // no value → not a data row
    const distRaw = distIdx >= 0 ? String(r[distIdx] ?? "").trim() : "";
    const st = matchState(ref, stateRaw, distRaw);
    if (!st) {
      skippedRows++;
      const k = distRaw ? `${stateRaw} / ${distRaw}` : stateRaw;
      unmatched.set(k, (unmatched.get(k) || 0) + 1);
      continue;
    }
    dims.begin();
    const flavor = dims.resolve("flavor", ref.flavorByName, flavorIdx, r);
    const size = dims.resolve("size", ref.sizeByName, sizeIdx, r);
    const pack = dims.resolve("pack", ref.packByName, pkgIdx, r);
    if (st.distributor) distributors.add(st.distributor);
    out.push({
      stateId: st.id, flavorId: flavor?.id ?? null, sizeId: size?.id ?? null,
      packSizeId: pack?.id ?? null,
      currentInventory: parseFloat(invCell.replace(/,/g, "")) || 0,
    });
    if (dims.commit()) dimMissRows++;
  }

  return {
    kind: "inventory",
    rows: out,
    periods: [],
    distributors: [...distributors],
    matchedRows: out.length, skippedRows, dimMissRows,
    unmatched: [...unmatched.entries()].sort((a, b) => b[1] - a[1]).slice(0, DIM_TOP_N),
    dimUnmatched: dims.summary(),
  };
}

// ── What state is the data actually in? (backlog APP-334) ────────────────────
// Five outcomes, five different sentences. The old code had ONE string for all
// of them — "Import failed." — which is how a wiped period read exactly like a
// rejected upload. The user cannot act until they know which happened.
// The `default:` arm is UNKNOWN, and it is deliberately the catch-all: anything
// we could not establish must land there rather than borrowing a more
// comfortable neighbour's wording.
function failureText(res, isDep) {
  const noun = isDep ? "depletions" : "inventory";
  const affected = isDep ? "months" : "rows";
  const n = (v) => Number(v || 0).toLocaleString();
  const s = (v) => (Number(v || 0) === 1 ? "" : "s");
  switch (res.outcome) {
    case IMPORT_OUTCOME.INTACT:
      return `Import failed — nothing was written and nothing was deleted. Your `
        + `existing ${noun} are exactly as they were, so it is safe to try again.`;
    case IMPORT_OUTCOME.INTACT_PLUS_ORPHANS:
      return `Import failed partway. Nothing was deleted, so none of your existing `
        + `${noun} were lost — but ${n(res.orphans)} newly written row${s(res.orphans)} `
        + `could not be taken back out, so the affected ${affected} may double-count `
        + `until you import this file again (a clean import replaces both copies).`;
    case IMPORT_OUTCOME.REPLACED_PLUS_STALE:
      return `All ${n(res.inserted)} row${s(res.inserted)} imported and nothing was `
        + `lost — but ${n(res.stale)} of the row${s(res.stale)} they replace could not `
        + `be deleted, so the affected ${affected} may double-count until you import `
        + `this file again.`;
    default:
      return `Import failed and the result could not be confirmed. New rows are `
        + `always written before anything is deleted, so your existing ${noun} `
        + `should still be there — but some of this file may have been written `
        + `too. Reload the page and check the affected ${affected} before `
        + `importing again, so you don't end up with two copies.`;
  }
}

/** One line for the page toast behind the modal. */
function shortWarning(res) {
  const n = (v) => Number(v || 0).toLocaleString();
  switch (res.outcome) {
    case IMPORT_OUTCOME.INTACT_PLUS_ORPHANS:
      return `⚠ Import failed — ${n(res.orphans)} stray rows left behind, nothing deleted`;
    case IMPORT_OUTCOME.REPLACED_PLUS_STALE:
      return `⚠ Imported, but ${n(res.stale)} superseded rows could not be removed`;
    default:
      return "⚠ Import result could not be confirmed — reload and check";
  }
}

export default function ImportModal({
  open, onClose, dataset = "depletions",
  states = [], flavors = [], sizes = [], packSizes = [], products = [],
  onImported,
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);

  const isDep = dataset === "depletions";
  const label = isDep ? "Depletions" : "Inventory";

  const reset = useCallback(() => {
    setPreview(null); setErr(null); setFileName("");
    if (fileRef.current) fileRef.current.value = "";
  }, []);

  const close = useCallback(() => { reset(); onClose?.(); }, [reset, onClose]);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(null); setBusy(true); setPreview(null); setFileName(file.name);
    try {
      const XLSX = await loadXLSX();
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      if (!ws) throw new Error("That file has no sheets.");
      const values = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: "" });
      const ref = buildLookups({ states, flavors, sizes, packSizes });
      setPreview(isDep ? parseDepletions(values, ref) : parseInventory(values, ref));
    } catch (e2) {
      setErr(e2?.message || "Could not read that file.");
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirm() {
    if (!preview || preview.rows.length === 0) return;
    setBusy(true); setErr(null);
    const report = (extra) => onImported?.({
      kind: preview.kind,
      inserted: preview.rows.length,
      skipped: preview.skippedRows,
      noSkuRows: preview.dimMissRows,            // backlog item 104
      periods: preview.periods.length,
      ...extra,
    });
    try {
      // Replace only what this file supersedes — for depletions that is the
      // periods it actually carries values for, never the whole table, so a
      // file covering Jan–Mar leaves Apr alone. The service writes the new rows
      // BEFORE retiring the old ones (backlog APP-334).
      const res = isDep
        ? await importDepletionsSafely({
            rows: preview.rows, periods: preview.periods, distributors: preview.distributors,
          })
        : await importInventorySafely({
            rows: preview.rows, distributors: preview.distributors,
          });

      if (res.ok) {
        report({ inserted: res.inserted });
        close();
        return;
      }

      // Not a clean replace. The user is told which of the four states their
      // data is in — that is the whole point of APP-334; "Import failed." on
      // its own left them unable to tell loss from a no-op.
      setErr(failureText(res, isDep));
      if (res.outcome !== IMPORT_OUTCOME.INTACT) {
        // Rows did change (or may have). Refresh the page behind the modal and
        // colour its toast, instead of leaving a stale grid reading as normal.
        report({ inserted: res.inserted, warning: shortWarning(res) });
      }
    } catch (e2) {
      // Only the pre-write steps throw — account lookup and reading which rows
      // are about to be superseded. Nothing has been written or deleted.
      setErr(`${e2?.message || "Import failed."} Nothing was written and nothing `
        + `was deleted — your existing ${label.toLowerCase()} are unchanged.`);
    } finally {
      setBusy(false);
    }
  }

  async function handleTemplate() {
    setBusy(true); setErr(null);
    try {
      if (isDep) await downloadDepletionsTemplate({ states, products });
      else await downloadInventoryTemplate({ states, products });
    } catch (e2) {
      setErr(e2?.message || "Could not build the template.");
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  const nothingMatched = preview && preview.rows.length === 0;
  const periodList = preview?.periods?.length
    ? preview.periods
        .slice().sort((a, b) => a.year - b.year || a.month - b.month)
        .map((p) => `${MONTH_ABBR[p.month - 1]} ${p.year}`)
    : [];

  return (
    // The backdrop and ✕ are inert while a commit is in flight, for the same
    // reason Cancel is: the write-then-swap (APP-334) now takes seconds rather
    // than two round trips, and dismissing the modal mid-import renders its
    // result — including the sentence saying what state the data is in — into
    // something nobody can see.
    <div
      className="dim-backdrop" role="dialog" aria-modal="true"
      aria-label={`Import ${label}`} aria-busy={busy}
      onClick={busy ? undefined : close}
    >
      <div className="dim-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dim-head">
          <h2>Import {label}</h2>
          <button className="dim-close" onClick={close} disabled={busy} aria-label="Close">×</button>
        </div>

        <div className="dim-body">
          <p className="dim-hint">
            Upload an Excel or CSV file. Rows are matched against your own States,
            Flavors, Sizes and Packages in System Settings — nothing is written
            until you've seen what matched.
          </p>

          <div className="dim-actions-row">
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: "none" }}
              onChange={handleFile}
            />
            <button className="dim-btn dim-btn-go" disabled={busy} onClick={() => fileRef.current?.click()}>
              {fileName ? "Choose a different file" : "Choose file"}
            </button>
            <button className="dim-btn dim-btn-cancel" disabled={busy} onClick={handleTemplate}>
              Download template
            </button>
            {fileName && <span className="dim-filename" title={fileName}>{fileName}</span>}
          </div>

          {busy && !preview && <p className="dim-muted">Reading…</p>}
          {err && <p className="dim-error">{err}</p>}

          {preview && (
            <div className="dim-preview">
              <div className="dim-stats">
                <div className="dim-stat">
                  <span className="dim-stat-num">{preview.rows.length.toLocaleString()}</span>
                  <span className="dim-stat-lbl">{isDep ? "values to import" : "rows to import"}</span>
                </div>
                <div className={`dim-stat${preview.skippedRows ? " dim-stat-warn" : ""}`}>
                  <span className="dim-stat-num">{preview.skippedRows.toLocaleString()}</span>
                  <span className="dim-stat-lbl">rows skipped</span>
                </div>
                {/* Backlog item 104. Kept, but attached to no SKU — a separate
                    failure from "skipped", and one that used to be invisible. */}
                <div className={`dim-stat${preview.dimMissRows ? " dim-stat-warn" : ""}`}>
                  <span className="dim-stat-num">{preview.dimMissRows.toLocaleString()}</span>
                  <span className="dim-stat-lbl">no SKU match</span>
                </div>
                {isDep && (
                  <div className="dim-stat">
                    <span className="dim-stat-num">{preview.periods.length}</span>
                    <span className="dim-stat-lbl">months affected</span>
                  </div>
                )}
              </div>

              {nothingMatched ? (
                // The item-26 guard, surfaced to the user instead of silently
                // destroying their data and reporting success.
                <p className="dim-error">
                  Nothing in this file matched your reference data, so there is
                  nothing to import and <strong>your existing {label.toLowerCase()} have not been
                  touched</strong>. Check that the State / Distributor / Flavor / Size /
                  Package names match System Settings.
                </p>
              ) : (
                <>
                  {isDep ? (
                    <p className="dim-note">
                      <strong>{periodList.join(", ")}</strong> will be replaced for your whole
                      account. Months not in this file are left alone.
                    </p>
                  ) : (
                    <p className="dim-note">
                      Inventory is a full snapshot — <strong>all existing inventory rows will be
                      replaced</strong> by the {preview.rows.length.toLocaleString()} rows in this file.
                    </p>
                  )}
                  {/* Backlog item 104 — lead with the SKU misses, because unlike a
                      skipped row these WILL be written and will look like real
                      data afterwards. Named values, not just a count: the fix is
                      one specific spreadsheet cell. */}
                  {preview.dimUnmatched.length > 0 && (
                    <div className="dim-unmatched dim-unmatched-warn">
                      <span className="dim-unmatched-title">
                        ⚠ {preview.dimMissRows.toLocaleString()} row
                        {preview.dimMissRows === 1 ? "" : "s"} will import with no SKU attached
                      </span>
                      <p className="dim-unmatched-note">
                        These Flavor / Bottle Size / Package values are not in System
                        Settings, so those rows belong to no product — they group under
                        “—” in the grid and never reach the Retail Product Finder. Fix
                        the spelling in your file (or add the SKU in System Settings)
                        and import again.
                      </p>
                      {preview.dimUnmatched.map((d) => (
                        <div className="dim-unmatched-dim" key={d.key}>
                          <span className="dim-unmatched-sub">
                            {d.label} — {d.rows.toLocaleString()} row{d.rows === 1 ? "" : "s"}
                          </span>
                          <ul>
                            {d.values.map(([v, n]) => (
                              <li key={v}>{v} <span className="dim-muted">× {n.toLocaleString()}</span></li>
                            ))}
                            {d.distinct > d.values.length && (
                              <li className="dim-muted">
                                +{(d.distinct - d.values.length).toLocaleString()} more value
                                {d.distinct - d.values.length === 1 ? "" : "s"}
                              </li>
                            )}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                  {preview.unmatched.length > 0 && (
                    <div className="dim-unmatched">
                      <span className="dim-unmatched-title">Skipped — no matching State / Distributor:</span>
                      {/* Backlog 331 — these used to be filed under whichever
                          distributor sold that state last, so name the pairing
                          rather than the state alone. */}
                      <p className="dim-unmatched-note">
                        A State + Distributor pairing that isn’t in System Settings is
                        skipped, never filed under a different distributor. Add the
                        pairing in System Settings → States, or fix the spelling.
                      </p>
                      <ul>
                        {preview.unmatched.map(([k, n]) => (
                          <li key={k}>{k} <span className="dim-muted">× {n}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="dim-foot">
          <button className="dim-btn dim-btn-cancel" disabled={busy} onClick={close}>Cancel</button>
          <button
            className="dim-btn dim-btn-go"
            disabled={busy || !preview || nothingMatched}
            onClick={handleConfirm}
          >
            {busy ? "Importing…" : `Import ${preview ? preview.rows.length.toLocaleString() : ""} row${preview && preview.rows.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
